import React, { useCallback, useEffect, useRef } from 'react';
import { withTranslation } from 'react-i18next';
import { VerticalLayoutDiv } from './styles';
import WidthResizableDiv from './WidthResizableDiv';
import VerticalCell from './VerticalLayout';
import DynamicContentCell from './DynamicContentCell';
import ComplexBlockRenderer from './ComplexBlockRenderer';
import { useDocumentContext, COMPLEX_DOCUMENT_CONTEXT_KEYS } from '../../context/complexDocumentContext';
import { useArtboardContext, CONTEXT_KEYS, TEMPLATE_TYPE } from '../../context/complexArtboardContext';
import {
  COMPLEX_BLOCK_TYPE,
  OVERFLOWING_BLOCK_TYPES,
  HEADER,
  FOOTER,
  CREATIVE_BLOCK,
  DYNAMIC_TABLE_BLOCK,
  DESIGN_FIXED_HEIGHT_BLOCKS,
  REUSABLE_SECTION,
  STATIC_TABLE_BLOCK,
  HTML_BLOCK,
  BUTTON_BLOCK,
  LINK_BLOCK,
} from '../../utils/complexDocumentConstants';
import { COMPLEX_PAGE_OPTIONS } from './translationValues';
import VisibilitySensor from 'react-visibility-sensor';
import { block_Selection } from '../../utils/helper';
import TocRepresentation from '../../components/TocRepresentation';

const dummyCallback = () => {};
const LayoutResizeObserver = ({ pageId, useStore, resizeCallback, customName = 'blockLayout' }) => {
  const [] = useStore(`complex-page-${pageId}`, resizeCallback, customName);

  return (<React.Fragment />);
}

const NAMESPACES = ['COMPLEX_PAGE_OPTIONS'];

const BlockLayout = ({ t, uuid, pageId, editable, useStore, customClassName, scaling, getElementStyles, isPageVisible, isEmailTemplate = false, isStandaloneFlow = false, isMobileView = false, isTocFlow= false }) => {

  const customSelector = useCallback((store) => store[uuid], []);
  const [pageDetails, methods] = useDocumentContext(customSelector, `blockLayout-${uuid}`);

  const customHeaderSelector = useCallback((store) => store[pageDetails[HEADER]]?.style?.height || 0, [pageDetails[HEADER]]);
  const [headerHeight] = useDocumentContext(customHeaderSelector, `blockLayout-header-${uuid}`);

  const customFooterSelector = useCallback((store) => store[pageDetails[FOOTER]]?.style?.height || 0, [pageDetails[FOOTER]]);
  const [footerHeight] = useDocumentContext(customFooterSelector, `blockLayout-footer-${uuid}`);

  const currentDesignSelector = useCallback((store) => [TEMPLATE_TYPE.DOCUMENT, TEMPLATE_TYPE.FLOW].includes(store[CONTEXT_KEYS.CURRENT_DESIGN]), []);
  const [isDocumentOrFlowDesign] = useArtboardContext(currentDesignSelector, '');

  const interactiveDesignSelector = useCallback(store => store[CONTEXT_KEYS.IS_INTERACTIVE_DESIGN], []);
  const [isInteractiveDesign] = useArtboardContext(interactiveDesignSelector, `BlockLayout`);

  const isOpenDesignSelector = useCallback(store => store[CONTEXT_KEYS.IS_OPEN_DESIGN], []);
  const [isOpenDesign] = useArtboardContext(isOpenDesignSelector, `BlockLayout`);

  const isPageVisibleRef = useRef(false);
  const onVisibilityCallback = useRef(null);
  const timer = useRef(undefined);

  const customCurrentBlockSelector = useCallback((store) => store[CONTEXT_KEYS.CURRENT_BLOCK],[]);
  const [currentEditingBlock] = useArtboardContext(customCurrentBlockSelector,`BlockRenderer-${uuid}`);
  const currentBlockTypeSelector = useCallback((store) => store[CONTEXT_KEYS.CURRENT_BLOCK_TYPE],[]);
  const [currentBlockType] = useArtboardContext(currentBlockTypeSelector,`BlockRenderer-${uuid}`);

  const page = pageDetails[pageId];
  const lastBlock = page.length > 0 ? page[page.length - 1] : undefined;

  const [resizable, updateArtboard] = useArtboardContext(
    (store) => store[CONTEXT_KEYS.PAGE_LAYOUT_VISIBLE] && store[CONTEXT_KEYS.CURRENT_PAGE] === uuid,
    `blockLayout-${uuid}`);

  const [currentBlockEditing] = useArtboardContext(
    (store) => (store[CONTEXT_KEYS.CURRENT_BLOCK] !== '' || store[CONTEXT_KEYS.CURRENT_BLOCK]) && store[CONTEXT_KEYS.CURRENT_PAGE] === uuid,
    `blockLayout-${uuid}`);

  const [fixedHeightBlock] = useArtboardContext(
    (store) => store[CONTEXT_KEYS.CURRENT_BLOCK_TYPE]
      ? DESIGN_FIXED_HEIGHT_BLOCKS.includes(store[CONTEXT_KEYS.CURRENT_BLOCK_TYPE])
      : false,
    `blockLayout-${uuid}`);

  const [currentBlockUUID] = useArtboardContext((store) => store[CONTEXT_KEYS.CURRENT_BLOCK], `blockLayout-${uuid}`);

  const occupied = useRef(false);
  const pageOverflowSelector = useCallback(store => store[CONTEXT_KEYS.ENABLE_PAGE_OVERFLOW], []);
  const [pageOverflowEnabled] = useArtboardContext(pageOverflowSelector, `blockLayout-${uuid}`);

  const selectedBlockSelector = useCallback(store => store[CONTEXT_KEYS.SELECTED_BLOCKS], []);
  const [selectedBlocks] = useArtboardContext(selectedBlockSelector, `blockLayout-${uuid}`);

  const selectedBlocksParentSelector = useCallback(
    store => store[CONTEXT_KEYS.SELECTED_BLOCKS_PARENT],
    [],
  );
  const [selectedBlocksParent] = useArtboardContext(
    selectedBlocksParentSelector,
    `blockLayout-${uuid}`,
  );

  const headerOrFooterSelectedSelector = useCallback(
    store => store[CONTEXT_KEYS.HEADER_OR_FOOTER_BLOCK_SELECTED],
    [],
  );
  const [isHeaderOrFooterSelected] = useArtboardContext(
    headerOrFooterSelectedSelector,
    `blockLayout-${uuid}`,
  );

  const contentSelectedSelector = useCallback(
    store => store[CONTEXT_KEYS.CONTENT_BLOCK_SELECTED],
    [],
  );
  const [isContentSelected] = useArtboardContext(
    contentSelectedSelector,
    `blockLayout-${uuid}`,
  );

  useEffect(() => {
    if (isPageVisible && onVisibilityCallback.current) {
      console.log("Calling overflow on visibility change!!!");
      onVisibilityCallback.current();
      onVisibilityCallback.current = null;
    }
    isPageVisibleRef.current = isPageVisible;
  }, [isPageVisible]);

  useEffect(() => {
    if (currentBlockEditing) {
      const myDiv = document.getElementById(`vertical-layout-${pageId}`);
      if (myDiv && lastBlock === currentEditingBlock && !OVERFLOWING_BLOCK_TYPES.includes(currentBlockType)) {
        myDiv.scrollTop = myDiv.scrollHeight - myDiv.clientHeight;
      } else {
        console.error(`Element with ID 'vertical-layout-${pageId}' not found.`);
      }
    }
  }, [currentBlockEditing]);

  useEffect(() => {
    if (!currentBlockEditing) return;
    const verticalLayoutRef = document.getElementById(`vertical-layout-${pageId}`);
    const editingContainer = document.getElementById(`editingContainer-${pageId}`);
    console.log("layout ref :: ", editingContainer);
    if (currentEditingBlock && verticalLayoutRef && currentBlockType === CREATIVE_BLOCK) {

      verticalLayoutRef.addEventListener('scroll', () => {
        const moveableControlBox = editingContainer.querySelector('.moveable-control-box:not(.block-resize)');
        if (moveableControlBox) {
          moveableControlBox.classList.add('hide');
        }
        if (timer.current) clearTimeout(timer.current);
        timer.current = setTimeout(() => {
          // TODO : Convey to creative block about change in scroll
          const blockReference = document.getElementById(currentEditingBlock);
          if (blockReference) {
            blockReference.dispatchEvent(new CustomEvent('pageScrollEnd',{detail:{}}));
          }
          if (moveableControlBox) {
            moveableControlBox.classList.remove('hide');
          }
          timer.current = undefined;
        }, 200);
      });
    }
  }, [currentBlockEditing, currentEditingBlock, currentBlockType])

  const resizeCallback = useCallback((entry) => {
    if (!entry) {
      const target = document.getElementById(`complex-page-${pageId}`);
      entry = {
        target,
        contentBoxSize: [
          {
            blockSize: target.getBoundingClientRect().height
          }
        ]
      };
    }
    const contentSize = entry.contentBoxSize[0];
    const maxHeight = parseFloat(entry.target.style.minHeight.replaceAll('px', ''));
    if (contentSize.blockSize > maxHeight) {
      console.log("entry resize ::: ", contentSize.blockSize, maxHeight, entry.target, pageId);
      console.log("height exceeded!! ", pageId);
      let growingHeight = 0;
      const children = Array.from(entry.target.children);
      let i = 0
      for (; i < children.length; i++) {
        if (growingHeight + children[i].offsetHeight > maxHeight) {
          break;
        }
        growingHeight += children[i].offsetHeight;
      }
      occupied.current = true;
      if (isPageVisibleRef.current) {
        methods.overflowPage(uuid, pageId, i, maxHeight, children, contentSize.blockSize);
      } else {
        onVisibilityCallback.current = () => methods.overflowPage(uuid, pageId, i, maxHeight, children, contentSize.blockSize);
      }
    } else {
      if (occupied.current === true) { // Added an intermediary to make sure that the toggle is made only on user action
        occupied.current = 1;
      } else if (occupied.current === 1) { // TODO : Check if all actions that can reduce the height passes this flow
        console.log("height increased!!! ", pageId);
        methods.onPageHeightExpand(uuid, pageId);
      }
    }
  }, []);

  const onDrop = useCallback((dragItem, dropItem, dropType = 'over') => {
    if (dragItem.type === REUSABLE_SECTION) {
      methods.set({
        [COMPLEX_DOCUMENT_CONTEXT_KEYS.CLONE_BLOCK]: {
          blockId: dragItem.id,
          blocks: dragItem.element.elementDetails,
          variableContract: dragItem.element?.variableContract || [],
          style: dragItem.element.style,
          uuid,
          pageId,
          dropType,
          dropItem,
          currentPageId: dragItem.pageId,
          sectionType: 'content'
        },
        [COMPLEX_DOCUMENT_CONTEXT_KEYS.NEW_LANGUAGE_CODE]: dragItem?.element?.language,
        [COMPLEX_DOCUMENT_CONTEXT_KEYS.IS_DRAG_ACTION]: true,
      });
      updateArtboard({
        [CONTEXT_KEYS.CONFIMATION_PAGE_MODAL]: true,
        [CONTEXT_KEYS.REUSABLE_TYPE]: REUSABLE_SECTION,
      });
    } else if (dragItem.type === STATIC_TABLE_BLOCK && !dragItem.element.elementDetails?.uuid) {
      methods.set({
        [COMPLEX_DOCUMENT_CONTEXT_KEYS.GRID_LAYOUT_BLOCK]: {
          uuid,
          pageId,
          draggedItem:dragItem,
          dropType,
          dropItem,
          currentPageId: dragItem.pageId,
        }
      });
    } else {
      const newBlockUUID = methods.setPage('addBlock', {
        uuid,
        block: Object.assign({}, dragItem.element.elementDetails),
        pageId,
        dropType,
        dropItem,
        currentPageId: dragItem.pageId,
      });
      if (newBlockUUID) {
        const elementBlocks = [DYNAMIC_TABLE_BLOCK, BUTTON_BLOCK, LINK_BLOCK];
        const artboardObj = {
          [CONTEXT_KEYS.CURRENT_BLOCK]: newBlockUUID,
          [CONTEXT_KEYS.CURRENT_BLOCK_TYPE]: dragItem.element.elementDetails.type,
          [CONTEXT_KEYS.CURRENT_PAGE]: uuid,
          [CONTEXT_KEYS.SWITCH_CREATIVE_DESIGN_MENU]:
            dragItem.element.elementDetails.type === CREATIVE_BLOCK,
          [CONTEXT_KEYS.ELEMENT_SETTINGS_VISIBLE]:
            elementBlocks.includes(dragItem.element.elementDetails.type),
        };
        if (dragItem.element.type === HTML_BLOCK) {
          artboardObj[CONTEXT_KEYS.CURRENT_BLOCK] = '';
          artboardObj[CONTEXT_KEYS.CURRENT_BLOCK_TYPE] = '';
          artboardObj[CONTEXT_KEYS.CURRENT_PAGE] = '';
        }
        updateArtboard(artboardObj);
      }
    }
  }, []);


  const onResizeStop = useCallback(({ left, right, top, bottom }) => {
    return methods.setFlowSettings('setPageMargin', {
      uuid,
      marginLeft: left,
      marginRight: right,
      marginTop: top,
      marginBottom: bottom
    });
  }, [uuid]);

  const blockSelection = useCallback(({itemUUID, parentUUID}) => {
    if (currentBlockEditing || isInteractiveDesign || isOpenDesign) return;
    block_Selection({
      selectedBlocks,
      itemUUID,
      parentUUID,
      flowDetails: methods.get()[parentUUID],
      selectedBlocksParent,
      isHeaderOrFooterSelected,
      isContentSelected,
      updateArtboard,
      methods,
      t,
    });
  }, [
    isContentSelected,
    isHeaderOrFooterSelected,
    methods,
    selectedBlocks,
    selectedBlocksParent,
    updateArtboard,
    t,
    currentBlockEditing,
    isInteractiveDesign
  ]);

  let pageBlocks = pageDetails[pageId];
  if (pageDetails[`limit-${pageId}`]) {
    console.log("pageDetails[`limit-${pageId}`] :: ", pageId, " ||| ", pageDetails[`limit-${pageId}`]);
    pageBlocks = pageBlocks.slice(0, pageDetails[`limit-${pageId}`] + 1);
  }

  let isOverflowVisible = currentBlockEditing && fixedHeightBlock ? pageBlocks.findIndex(block => block === currentBlockUUID) : -1;
  if (isEmailTemplate) {
    isOverflowVisible = true;
  } else {
    isOverflowVisible = false;
  }
  const width = isEmailTemplate ? isMobileView ? 390 : pageDetails?.width : 'none';
  const height = isEmailTemplate ? 'fit-content' : `${pageDetails?.height - (headerHeight + footerHeight)}px`;

  return (
    <>
    {pageOverflowEnabled && !isEmailTemplate ? <LayoutResizeObserver uuid={uuid} pageId={pageId} useStore={useStore} resizeCallback={resizeCallback} customName={'overflow'} /> : null}
    <VerticalLayoutDiv className={customClassName} id={`vertical-layout-${pageId}`} style={{
      position: 'relative',
      height,
      width,
        overflow: pageOverflowEnabled ? 'hidden' : isOverflowVisible ? 'visible' : 'hidden auto',
    }}>
    <WidthResizableDiv
      customId={`complex-page-${pageId}`}
      width={width - (pageDetails?.margin[1] + pageDetails?.margin[3])}
      height={pageDetails?.height - (headerHeight + footerHeight) - ((pageDetails?.margin[0] || 0) + (pageDetails?.margin[2] || 0))}
      margin={pageDetails.margin}
      onResizeStop={onResizeStop}
      resizable={resizable}
      pageOverflowEnabled={!isEmailTemplate && pageOverflowEnabled}
    >
    {isPageVisible ? ( <>
      {(pageDetails?.header === undefined && pageDetails?.footer === undefined && pageBlocks?.length === 0)
        ? (
         <VerticalCell
           uuid={'empty-layout'}
           onDrop={onDrop}
           editable={editable}
           type={isDocumentOrFlowDesign ?[...COMPLEX_BLOCK_TYPE, REUSABLE_SECTION] : COMPLEX_BLOCK_TYPE}
           className={'droppable empty'}
           enableDropOver={true}
           currentBlockEditing={currentBlockEditing}
           t={t}
         >
           {t(COMPLEX_PAGE_OPTIONS.EMPTY_PAGE_PLACEHOLDER.key, COMPLEX_PAGE_OPTIONS.EMPTY_PAGE_PLACEHOLDER.value)}
         </VerticalCell>
        )
        : (
          <>
          {pageBlocks?.map((blockUUID, index) => (
            <VerticalCell
              key={`layout-${blockUUID}`}
              uuid={blockUUID}
              onDrop={onDrop}
              editable={editable}
              currentBlockEditing={currentBlockEditing}
              type={isDocumentOrFlowDesign ?[...COMPLEX_BLOCK_TYPE, REUSABLE_SECTION] : COMPLEX_BLOCK_TYPE}
              t={t}
            >
              {/*renderer(element)*/}
              <DynamicContentCell
                editable={editable && pageBlocks?.length > 1 && !currentBlockEditing}
                isInteractiveDesign={isInteractiveDesign}
                itemUUID={blockUUID}
                pageId={pageId}
              >
                <VisibilitySensor
                  key={blockUUID}
                  partialVisibility={true}
                  id={blockUUID}
                  active={!pageOverflowEnabled}
                >
                  {({ isVisible }) => {
                    return(
                    <ComplexBlockRenderer
                      key={blockUUID}
                      itemUUID={blockUUID}
                      editable={isTocFlow ? index === 0 : editable}
                      parentUUID={uuid}
                      pageId={pageId}
                      resizeCallback={pageOverflowEnabled ? resizeCallback : dummyCallback}
                      scaling={scaling}
                      isVisible={isVisible}
                      getElementStyles={getElementStyles}
                      lastBlock={index === pageBlocks.length - 1}
                      useStore={useStore}
                      onClickBlockSelection= {blockSelection}
                      isStandaloneFlow={isStandaloneFlow}
                      isMobileView={isMobileView}
                    />
                    )
                  }}
                </VisibilitySensor>
              </DynamicContentCell>
            </VerticalCell>
          ))
          }
          {isTocFlow && pageBlocks.length === 1 && <TocRepresentation/>}
          </>
        )
      }
      </>) : <React.Fragment/>}
    </WidthResizableDiv>
    </VerticalLayoutDiv>
    </>
  )
}

export default React.memo(withTranslation(NAMESPACES)(BlockLayout));